import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { theme, DatePicker, Row, Tooltip, Col } from 'antd';
import { AppDispatch, RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import { useLocation } from 'react-router-dom';
import { TitleList } from 'store/ducks/globalTypes';
import qs from 'qs';
import ReportModal from 'components/Modal/AdditionalModal';
import { tableDataArticle8 } from 'constants/excelData';
import useFilters from 'utils/hooks/useFilters';
import { globalOp } from 'store/ducks/Global';
import {
  set_selectedDate,
  set_selectedICTSystemByFunction,
  set_selectedICTSystembyICTService,
} from 'store/ducks/Reportings/reportingSlice';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { ReportingHoverStyle } from 'components/Tables/Reporting/Reporting.styled';
import { SystemOption } from 'store/ducks/Reportings/types';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { contractual_informationOp } from 'store/ducks/DataEntry/ContractualInformation';
import { useTranslation } from 'react-i18next';
import * as Styled from '../Layout.styled';

const { useToken } = theme;

const ReportingLayout: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);

  useSelector((state: RootState) => state.reportings);
  const [entityMaintaining, setEntityMaintaining] = useState<any[]>([]);
  const location = useLocation();
  const [systemsForFunctions, setSystemsForFunctions] = useState<
    SystemOption[]
  >([]);
  const [selectedICTSystemByFunction, setSelectedICTSystemByFunction] =
    useState<number | null>(null);
  const [systemsForICTServices, setSystemsForICTServices] = useState<
    SystemOption[]
  >([]);
  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const [selectedEntity, setSelectedEntity] = useState(
    queryParams.entity || null,
  );
  const [ictData, setICTData] = useState<any[]>([]);
  const [selectedICTSystemByICTServices, setSelectedICTSystemByICTServices] =
    useState<number | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState();
  const { query, handleFilterChangeForReportings, extractedOptions } =
    useFilters();

  const fetchICTServicesByEntity = async () => {
    try {
      const response =
        await contractual_informationOp.fetchICTServicesbyEntityName({
          entity_id: selectedEntity,
          entity_name: selectedEntityName,
        });
      const data = response?.data;
      if (data.length > 0) {
        const updatedICTServicedData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          ict_services_name: item.ict_services_name,
          description: item.description,
        }));
        setICTData(updatedICTServicedData);
      } else {
        setICTData([]);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const fetchICTSystemsCategoryFunction = async (
    entityId: any,
    entityName: any,
  ) => {
    try {
      const response =
        await functionOp.fetchICTSystemsCategoryFunctionbyEntityName({
          entity_id: entityId,
          entity_name: entityName,
        });
      if (response?.data) {
        const formattedOptions: SystemOption[] = response.data.map(
          (system: { name: string; id: number }) => ({
            label: system.name,
            value: system.id,
            key: system.id,
          }),
        );
        setSystemsForFunctions(formattedOptions);
      }
    } catch (error) {
      console.log('Error fetching ICT systems:', error);
    }
  };

  const fetchICTSystemsCategoryICTServices = async (
    entityId: any,
    entityName: any,
  ) => {
    try {
      const response =
        await ict_serviceOp.fetchICTSystemsCategoryICTServicesbyEntityName({
          entity_id: entityId,
          entity_name: entityName,
        });
      if (response?.data) {
        const formattedOptions: SystemOption[] = response.data.map(
          (system: { name: string; id: number }) => ({
            label: system.name,
            value: system.id,
            key: system.id,
          }),
        );
        setSystemsForICTServices(formattedOptions);
      }
    } catch (error) {
      console.log('Error fetching ICT systems:', error);
    }
  };

  const fetchEntityMaintaining = async () => {
    try {
      const response = await entityOp.fetchAllFinancialEntities();
      const data = response?.data;
      if (data.length > 0) {
        const updatedTPPData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          financial_entity_name: item.financial_entity_name,
        }));
        setEntityMaintaining(updatedTPPData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onChangeStartDate = (
    date: dayjs.Dayjs | null,
    dateString: string | string[],
  ) => {
    if (typeof dateString === 'string') {
      dispatch(set_selectedDate(dateString));
    }
  };

  useEffect(() => {
    dispatch(globalOp.setFilters());
  }, [dispatch]);

  useEffect(() => {
    fetchEntityMaintaining();
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      fetchICTServicesByEntity();
      fetchICTSystemsCategoryFunction(selectedEntity, query.entity);
      fetchICTSystemsCategoryICTServices(selectedEntity, query.entity);
    }
  }, [selectedEntity, query]);

  const handleEntityChange = (
    value: any,
    option?: { value: any; label: any; key: any },
  ) => {
    if (!value || !option) {
      setSelectedEntity(null);
      return;
    }

    setSelectedEntity(option.value);
    setSelectedEntityName(option.label);
    handleFilterChangeForReportings(option.value, option.label);
  };

  const handleSelectICTSystemByFunctionChange = (value: number) => {
    setSelectedICTSystemByFunction(value);
    dispatch(set_selectedICTSystemByFunction(value));
  };

  const handleSelectICTSystemByICTServicesChange = (value: number) => {
    setSelectedICTSystemByICTServices(value);
    dispatch(set_selectedICTSystembyICTService(value));
  };

  return (
    <Styled.StyledLayout token={token}>
      {(scheme === 'firstTheme' || scheme === 'eighthTheme') && (
        <ReportingHoverStyle />
      )}

      <TitleBar title={t(TitleList.ARTICLE_8_REPORT)} />

      <Styled.FilterCard theme={scheme}>
        <Styled.FilterTitle theme={scheme}>
          {t('ARTICLE_8_REPORTING.REPORTING_PARAMETERS')}
        </Styled.FilterTitle>

        <Styled.FilterGrid>
          {/* Entity */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ARTICLE_8_REPORTING.SELECT_ENTITY_REPORTED')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              placeholder={t('ARTICLE_8_REPORTING.SELECT_ENTITY_PLACEHOLDER')}
              allowClear
              showSearch
              options={extractedOptions}
              value={queryParams.entity}
              onChange={handleEntityChange}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* Reference Date */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ARTICLE_8_REPORTING.SELECT_REPORTING_DATE')}
            </Styled.FilterLabel>

            <Styled.SmallDatePicker>
              <DatePicker onChange={onChangeStartDate} />
            </Styled.SmallDatePicker>
          </Styled.FilterItem>

          {/* ICT System by Functions */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ARTICLE_8_REPORTING.ICT_SYSTEM_BY_FUNCTIONS')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              options={systemsForFunctions}
              value={
                systemsForFunctions.find(
                  (opt) => opt.value === selectedICTSystemByFunction,
                ) ?? undefined
              }
              onChange={handleSelectICTSystemByFunctionChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* ICT System by ICT Service */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ARTICLE_8_REPORTING.ICT_SYSTEM_BY_ICT_SERVICE')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              options={systemsForICTServices}
              value={
                systemsForICTServices.find(
                  (opt) => opt.value === selectedICTSystemByICTServices,
                ) ?? undefined
              }
              onChange={handleSelectICTSystemByICTServicesChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>
        </Styled.FilterGrid>
      </Styled.FilterCard>

      <Styled.HorizontalLayout theme={scheme}>
        <Styled.GridContainer token={token}>
          <Styled.Container>
            <Row>
              <Col>
                <Styled.SectionTitle token={token}>
                  {t('ARTICLE_8_REPORTING.ICT_INVENTORIES')}
                </Styled.SectionTitle>

                <Styled.Section section="Reports" token={token}>
                  <Styled.List>
                    {tableDataArticle8.map((item) => (
                      <Tooltip
                        key={item.title}
                        title={t(item.description)}
                        placement="right"
                      >
                        <Styled.CardLine theme={scheme}>
                          <div>
                            <ReportModal
                              title={t(item.title)}
                              tableNumber={t(item.title)}
                              headers={[]}
                            />
                          </div>
                        </Styled.CardLine>
                      </Tooltip>
                    ))}
                  </Styled.List>
                </Styled.Section>
              </Col>
            </Row>
          </Styled.Container>
        </Styled.GridContainer>
      </Styled.HorizontalLayout>
    </Styled.StyledLayout>
  );
};

export default ReportingLayout;
